import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  FaEnvelope,
  FaPhone,
  FaUser,
  FaCalendar,
  FaTrash,
  FaEye,
  FaCheckCircle,
  FaInbox,
  FaReply,
  FaArchive,
  FaCar,
  FaClock
} from 'react-icons/fa';
import { contactAPI } from '../../services/api';
import { toast } from 'react-toastify';
import './ContactSubmissions.css';

const ContactSubmissions = () => {
  const navigate = useNavigate(); // Add this line
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [filter, setFilter] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await contactAPI.getAll(params);
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadStats = useCallback(async () => {
    try {
      const response = await contactAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  useEffect(() => {
    loadContacts();
    loadStats();
  }, [loadContacts, loadStats]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await contactAPI.updateStatus(id, newStatus);
      if (response.data.success) {
        toast.success('Status updated successfully');
        loadContacts();
        loadStats();
        if (selectedContact?.id === id) {
          setSelectedContact(response.data.data);
        }
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const response = await contactAPI.delete(id);
      if (response.data.success) {
        toast.success('Contact submission deleted');
        setContacts(contacts.filter(c => c.id !== id));
        setSelectedContact(null);
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const viewDetails = async (contact) => {
    try {
      setLoadingDetails(true);
      const response = await contactAPI.getById(contact.id);
      if (response.data.success) {
        setSelectedContact(response.data.data);
        loadContacts();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to load contact details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new':
        return 'status-new';
      case 'read':
        return 'status-read';
      case 'replied':
        return 'status-replied';
      case 'archived':
        return 'status-archived';
      default:
        return '';
    }
  };
return (
  <div className="contact-submissions-container">
    <div className="page-header">
      <div className="header-content">
        <motion.button
          className="back-button"
          onClick={() => navigate('/admin/dashboard')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft /> Back to Dashboard
        </motion.button>
        <div className="header-text">
          <h1>Contact Submissions</h1>
          <p>Manage customer inquiries and messages</p>
        </div>
      </div>
    </div>
  


      <div className="page-content">
        {/* Statistics Cards */}
        <motion.div
          className="stats-grid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="stat-icon total">
            <FaInbox />
          </div>
          <div className="stat-info">
            <h3>{stats.total}</h3>
            <p>Total Submissions</p>
          </div>
        </motion.div>
        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="stat-icon new">
            <FaEnvelope />
          </div>
          <div className="stat-info">
            <h3>{stats.new}</h3>
            <p>New Messages</p>
          </div>
        </motion.div>
        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="stat-icon read">
            <FaEye />
          </div>
          <div className="stat-info">
            <h3>{stats.read}</h3>
            <p>Read</p>
          </div>
        </motion.div>
        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="stat-icon replied">
            <FaCheckCircle />
          </div>
          <div className="stat-info">
            <h3>{stats.replied}</h3>
            <p>Replied</p>
          </div>
        </motion.div>
      </motion.div>

      {/* Filters */}
      <motion.div
        className="filters"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <motion.button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaInbox /> All
        </motion.button>
        <motion.button
          className={`filter-btn ${filter === 'new' ? 'active' : ''}`}
          onClick={() => setFilter('new')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaEnvelope /> New ({stats.new})
        </motion.button>
        <motion.button
          className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
          onClick={() => setFilter('read')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaEye /> Read ({stats.read})
        </motion.button>
        <motion.button
          className={`filter-btn ${filter === 'replied' ? 'active' : ''}`}
          onClick={() => setFilter('replied')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaReply /> Replied ({stats.replied})
        </motion.button>
        <motion.button
          className={`filter-btn ${filter === 'archived' ? 'active' : ''}`}
          onClick={() => setFilter('archived')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaArchive /> Archived
        </motion.button>
      </motion.div>

      {/* Contacts List */}
      <div className="contacts-layout">
        <div className="contacts-list">
          <div className="contacts-pane-header">
            <div>
              <h2>Inbox</h2>
              <p>{contacts.length} visible submissions</p>
            </div>
          </div>
          {loading ? (
            <div className="loading-container">
              <div className="loader"></div>
            </div>
          ) : contacts.length === 0 ? (
            <div className="no-contacts">
              <FaEnvelope size={50} color="#ccc" />
              <p>No contact submissions found</p>
            </div>
          ) : (
            contacts.map((contact) => (
              <motion.div
                key={contact.id}
                className={`contact-item ${selectedContact?.id === contact.id ? 'active' : ''}`}
                onClick={() => viewDetails(contact)}
                whileHover={{ scale: 1.01 }}
              >
                <div className="contact-item-header">
                  <div className="contact-item-name">
                    <FaUser />
                    <strong>{contact.name}</strong>
                  </div>
                  <span className={`status-badge ${getStatusBadgeClass(contact.status)}`}>
                    {contact.status}
                  </span>
                </div>
                <div className="contact-item-info">
                  <div className="contact-item-detail">
                    <FaEnvelope size={12} />
                    <span>{contact.email}</span>
                  </div>
                  <div className="contact-item-detail">
                    <FaPhone size={12} />
                    <span>{contact.phone}</span>
                  </div>
                </div>
                {contact.interest && (
                  <div className="contact-item-interest">
                    <FaCar size={12} /> Interested in: <strong>{contact.interest}</strong>
                  </div>
                )}
                <div className="contact-item-date">
                  <FaCalendar size={12} />
                  <span>{formatDate(contact.createdAt)}</span>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Contact Details Panel */}
        {loadingDetails ? (
          <div className="contact-details">
            <div className="select-contact-prompt">
              <FaClock />
              <h3>Loading Contact</h3>
              <p>Fetching the latest contact details</p>
            </div>
          </div>
        ) : selectedContact ? (
          <motion.div
            className="contact-details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="contacts-pane-header contacts-pane-header-details">
              <div>
                <h2>Details</h2>
                <p>Review and manage this submission</p>
              </div>
            </div>
            <div className="details-header">
              <h2><FaUser /> Contact Details</h2>
              <button
                className="close-details"
                onClick={() => setSelectedContact(null)}
              >
                ×
              </button>
            </div>

            <div className="details-body">
              <div className="contact-info-grid">
                <div className="detail-section">
                  <label><FaUser /> Name</label>
                  <p>{selectedContact.name}</p>
                </div>

                <div className="detail-section">
                  <label><FaEnvelope /> Email</label>
                  <p>
                    <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
                  </p>
                </div>

                <div className="detail-section">
                  <label><FaPhone /> Phone</label>
                  <p>
                    <a href={`tel:${selectedContact.phone}`}>{selectedContact.phone}</a>
                  </p>
                </div>

                {selectedContact.interest && (
                  <div className="detail-section">
                    <label><FaCar /> Interested In</label>
                    <p style={{ textTransform: 'capitalize' }}>{selectedContact.interest}</p>
                  </div>
                )}
              </div>

              {selectedContact.message && (
                <div className="detail-section">
                  <label><FaEnvelope /> Message</label>
                  <p className="message-content">{selectedContact.message}</p>
                </div>
              )}

              <div className="detail-section">
                <label><FaCheckCircle /> Status</label>
                <select
                  value={selectedContact.status}
                  onChange={(e) => handleStatusChange(selectedContact.id, e.target.value)}
                  className="status-select"
                >
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="detail-section">
                <label><FaClock /> Submitted</label>
                <p>{formatDate(selectedContact.createdAt)}</p>
              </div>

              <div className="details-actions">
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(selectedContact.id)}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="contact-details">
            <div className="contacts-pane-header contacts-pane-header-details">
              <div>
                <h2>Details</h2>
                <p>Select a submission to inspect it</p>
              </div>
            </div>
            <div className="select-contact-prompt">
              <FaEnvelope />
              <h3>Select a Contact</h3>
              <p>Click on a contact from the list to view details</p>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default ContactSubmissions;
